import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userApi from '~/apis/user';
import { getErrorMessage } from '~/utils/helper/error';

type Props = {
  onClose: () => void;
};

// Same rule as the backend: at least 8 characters with a letter and a number.
const isStrong = (password: string) => password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-60';

const ChangePasswordModal = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string>();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && !saving && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  const errors = {
    current: !current ? t('pages.setup.required') : undefined,
    next: !next
      ? t('pages.setup.required')
      : !isStrong(next)
      ? t('pages.account.passwordRule')
      : next === current
      ? t('pages.account.passwordSame')
      : undefined,
    confirm: confirm !== next ? t('pages.account.passwordMismatch') : undefined,
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setServerError(undefined);
    if (errors.current || errors.next || errors.confirm) return;

    setSaving(true);
    try {
      await userApi.changePassword({ currentPassword: current, newPassword: next });
      setDone(true);
    } catch (error) {
      setServerError(getErrorMessage(error) ?? t('pages.account.passwordFailed'));
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, value: string, onChange: (value: string) => void, error?: string, autoFocus = false) => (
    <label className='flex flex-col gap-1 text-sm'>
      <span>{label} *</span>
      <input
        type='password'
        autoComplete={label === t('pages.account.currentPassword') ? 'current-password' : 'new-password'}
        autoFocus={autoFocus}
        className={`${inputClass} ${submitted && error ? 'border-destructive' : ''}`}
        value={value}
        onChange={(e) => {
          setServerError(undefined);
          onChange(e.target.value);
        }}
      />
      {submitted && error && <span className='text-xs text-destructive'>{error}</span>}
    </label>
  );

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
      onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        role='dialog'
        aria-modal='true'
        aria-label={t('pages.account.changePassword')}
        className='flex w-full max-w-md flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-xl'
      >
        <h2 className='text-base font-semibold text-foreground'>{t('pages.account.changePassword')}</h2>

        {done ? (
          <>
            <p className='text-sm text-foreground' role='status'>
              {t('pages.account.passwordChanged')}
            </p>
            <div className='flex justify-end'>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90'
              >
                {t('pages.setup.close')}
              </button>
            </div>
          </>
        ) : (
          <>
            {field(t('pages.account.currentPassword'), current, setCurrent, errors.current, true)}
            {field(t('pages.account.newPassword'), next, setNext, errors.next)}
            {field(t('pages.account.confirmPassword'), confirm, setConfirm, errors.confirm)}
            {serverError && (
              <p className='text-sm text-destructive' role='alert'>
                {serverError}
              </p>
            )}
            <div className='flex justify-end gap-2'>
              <button
                type='button'
                onClick={onClose}
                disabled={saving}
                className='rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50'
              >
                {t('pages.setup.cancel')}
              </button>
              <button
                type='submit'
                disabled={saving}
                className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50'
              >
                {saving ? t('pages.account.saving') : t('pages.account.save')}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default ChangePasswordModal;
